// Courrier type : trame du cabinet, champs du dossier, édition, enregistrement, envoi.
import { useEffect, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Mail, Save, X } from 'lucide-react';
import { useAuthStore } from '../../store/useAuthStore';
import { useDossier } from '../../hooks/queries/useDossiers';
import { useDossierContacts } from '../../hooks/queries/useContacts';
import { useDocumentTemplates, useGenerateDocument, usePdfLayouts, useSaveDraft, docKeys } from '../../hooks/queries/useDocumentGeneration';
import { useTenantBranding } from '../../contexts/ThemeContext';
import { finalizeDraft, sendClientLetter, type DocumentDraft } from '../../services/documents/documentService';
import { extractMarkers, markdownToTiptap, tiptapText, tiptapToPdfBlocks, type TiptapNode } from '../../services/documents/markdownDoc';
import { resolveLayoutTokens } from '../../services/documents/pdfLayoutService';
import { uploadFile } from '../../services/files/filesService';
import { DOSSIER_CONTACT_ROLE_LABELS, type DossierContactRole } from '../../services/contacts/contactService';
import { DocumentEditor } from './DocumentEditor';
import { buildDocumentPdfBlob, type DocumentPdfProps } from './DocumentPdf';
import { LetterPreview } from './LetterPreview';

function useDebouncedValue<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = window.setTimeout(() => setDebounced(value), delayMs);
    return () => window.clearTimeout(id);
  }, [value, delayMs]);
  return debounced;
}

function contactLabel(c: {
  company_name: string | null;
  first_name: string | null;
  last_name: string | null;
}): string {
  return c.company_name ?? ([c.first_name, c.last_name].filter(Boolean).join(' ') || 'Contact');
}

function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const raw = String(reader.result ?? '');
      resolve(raw.slice(raw.indexOf(',') + 1));
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(blob);
  });
}

function draftContentToDoc(content: unknown): TiptapNode | null {
  if (!content || typeof content !== 'object') return null;
  const c = content as { format?: string; md?: string; type?: string };
  if (c.format === 'markdown' && typeof c.md === 'string') return markdownToTiptap(c.md);
  if (c.type === 'doc') return c as TiptapNode;
  return null;
}

export function LetterComposer({
  dossierId,
  existingDraft = null,
  onClose,
}: {
  dossierId: string;
  existingDraft?: DocumentDraft | null;
  onClose: () => void;
}) {
  const collaborator = useAuthStore((s) => s.collaborator);
  const { branding } = useTenantBranding();
  const { data: dossier } = useDossier(dossierId);
  const { data: links = [] } = useDossierContacts(dossierId);
  const { data: templates = [] } = useDocumentTemplates(true);
  const { data: layouts } = usePdfLayouts();
  const letters = templates;
  const generate = useGenerateDocument();
  const saveDraft = useSaveDraft(dossierId);
  const qc = useQueryClient();

  const clientLink = links.find((l) => l.role === 'client');
  const [contactId, setContactId] = useState<string>(clientLink?.contact_id ?? '');
  const [templateId, setTemplateId] = useState<string | null>(existingDraft?.template_id ?? null);
  const resumed = existingDraft ? draftContentToDoc(existingDraft.content) : null;
  const [draftId, setDraftId] = useState<string | null>(existingDraft?.id ?? null);
  const [title, setTitle] = useState(existingDraft?.title ?? '');
  const [doc, setDoc] = useState<TiptapNode | null>(resumed);
  const [docText, setDocText] = useState(resumed ? tiptapText(resumed) : '');
  const [editorKey, setEditorKey] = useState(0);
  const [sending, setSending] = useState(false);

  const selected = links.find((l) => l.contact_id === (contactId || clientLink?.contact_id));
  const markers = useMemo(() => extractMarkers(docText), [docText]);
  const previewDoc = useDebouncedValue(doc, 650);
  const previewTitle = useDebouncedValue(title, 650);

  const layout = useMemo(() => {
    const tpl = letters.find((t) => t.id === templateId);
    const row =
      (tpl?.pdf_layout_id ? layouts?.find((l) => l.id === tpl.pdf_layout_id) : undefined)
      ?? layouts?.find((l) => l.is_default);
    return resolveLayoutTokens(row);
  }, [letters, templateId, layouts]);

  const recipientLines = useMemo(() => {
    if (!selected) return [] as string[];
    const name = contactLabel(selected.contact);
    const town = [selected.contact.postal_code, selected.contact.city].filter(Boolean).join(' ');
    return [name, selected.contact.address_line1, selected.contact.address_line2, town].filter((l): l is string => !!l);
  }, [selected]);

  const pdfPropsFor = (source: TiptapNode, subject: string): DocumentPdfProps => ({
    firmName: branding.firm_name || 'CIME Avocats',
    logoUrl: branding.logo_url,
    colorPrimary: branding.color_primary,
    colorGold: branding.color_gold,
    title: subject,
    recipientLines,
    dateLine: `Le ${new Date().toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}`,
    blocks: tiptapToPdfBlocks(source),
    signerName: collaborator?.full_name ?? '',
    layout,
  });

  const previewProps = useMemo(
    () => (previewDoc ? pdfPropsFor(previewDoc, previewTitle) : null),
    // Recalculé seulement quand le texte débouncé ou la mise en page change.
    [previewDoc, previewTitle, branding, recipientLines, layout, collaborator],
  );

  const openLetter = (id: string) => {
    const recipient = contactId || clientLink?.contact_id;
    if (!recipient) {
      toast.error('Ajoutez un client sur le dossier avant de préparer le courrier.');
      return;
    }
    setTemplateId(id);
    generate.mutate(
      { dossierId, templateId: id, directives: '', contactIds: [recipient], mergeOnly: true },
      {
        onSuccess: (r) => {
          const next = markdownToTiptap(r.content_md);
          setDraftId(r.draft_id);
          setTitle(r.title);
          setDoc(next);
          setDocText(tiptapText(next));
          setEditorKey((k) => k + 1);
        },
        onError: (e) => toast.error(e instanceof Error ? e.message : 'Le courrier n’a pas pu être préparé.'),
      },
    );
  };

  const persist = async () => {
    if (!draftId || !doc) return;
    await saveDraft.mutateAsync({ id: draftId, patch: { title, content: doc } });
    toast.success('Courrier enregistré.');
  };

  const send = async () => {
    if (!draftId || !doc || !collaborator || !selected) return;
    if (markers.length > 0) {
      toast.error('Complétez les passages entre crochets avant l’envoi.');
      return;
    }
    const email = selected.contact.email?.trim();
    if (!email) {
      toast.error('Ce contact n’a pas d’adresse e-mail.');
      return;
    }
    setSending(true);
    try {
      await saveDraft.mutateAsync({ id: draftId, patch: { title, content: doc } });
      const blob = await buildDocumentPdfBlob(pdfPropsFor(doc, title));
      const safeName = `${title.replace(/[\\/:*?"<>|]/g, '-').slice(0, 120)}.pdf`;
      const file = new File([blob], safeName, { type: 'application/pdf' });
      const stored = await uploadFile({
        dossierId,
        folderId: null,
        uploaderId: collaborator.id,
        file,
        source: 'ai_generated',
        sourceRef: `draft::${draftId}`,
      });
      await finalizeDraft(draftId, stored.id);
      const pdfBase64 = await blobToBase64(blob);
      const sent = await sendClientLetter({
        dossierId,
        contactId: selected.contact_id,
        subject: title || 'Courrier',
        filename: safeName,
        pdfBase64,
      });
      qc.invalidateQueries({ queryKey: docKeys.drafts(dossierId) });
      qc.invalidateQueries({ queryKey: ['dossier-files'] });
      toast.success(`Courrier envoyé à ${sent.to}.`);
      onClose();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'L’envoi a échoué.');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-cime-modal>
      <div className="flex h-[min(92vh,920px)] w-full max-w-6xl flex-col overflow-hidden rounded-cime bg-white shadow-xl">
        <div className="flex items-center justify-between border-b border-neutral-200 px-5 py-3">
          <h2 className="text-sm font-semibold text-cime-text-primary">
            {doc ? title || 'Document' : 'Nouveau document'}
          </h2>
          <button type="button" className="text-neutral-400 hover:text-neutral-700" onClick={onClose} aria-label="Fermer">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className={doc ? 'flex min-h-0 flex-1 flex-col lg:flex-row' : 'min-h-0 flex-1 overflow-y-auto px-5 py-4'}>
          {!doc && (
            <div className="space-y-4">
              <p className="text-sm text-neutral-500">
                {dossier ? `${dossier.number} — ${dossier.title}` : 'Dossier'}
              </p>
              <label className="block text-sm">
                <span className="font-medium text-neutral-700">Destinataire</span>
                <select
                  className="mt-1 w-full rounded border border-neutral-200 px-2 py-1.5"
                  value={contactId || clientLink?.contact_id || ''}
                  onChange={(e) => setContactId(e.target.value)}
                >
                  {links.map((l) => (
                    <option key={l.contact_id} value={l.contact_id}>
                      {contactLabel(l.contact)}
                      {l.role ? ` — ${DOSSIER_CONTACT_ROLE_LABELS[l.role as DossierContactRole] ?? l.role}` : ''}
                    </option>
                  ))}
                </select>
              </label>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                {letters.map((t) => (
                  <button
                    key={t.id}
                    type="button"
                    disabled={generate.isPending}
                    className="rounded border border-neutral-200 p-4 text-left hover:border-cime-primary disabled:opacity-50"
                    onClick={() => openLetter(t.id)}
                  >
                    <p className="text-sm font-semibold text-neutral-800">{t.name}</p>
                    {t.description && <p className="mt-1 text-xs text-neutral-500">{t.description}</p>}
                  </button>
                ))}
                {letters.length === 0 && (
                  <p className="text-sm text-neutral-500">Aucun modèle pour le moment.</p>
                )}
              </div>
              {templateId && generate.isPending && (
                <p className="text-sm text-neutral-500">Préparation du courrier…</p>
              )}
            </div>
          )}

          {doc && (
            <>
              <div className="min-h-0 flex-1 overflow-y-auto bg-neutral-100 px-5 py-4">
                <div className="space-y-3">
                  <label className="block">
                    <span className="text-xs font-medium text-neutral-500">Objet</span>
                    <input
                      className="mt-0.5 w-full rounded-cime border border-neutral-200 bg-white px-2 py-1.5 text-sm font-medium"
                      value={title}
                      onChange={(e) => setTitle(e.target.value)}
                    />
                  </label>
                  {markers.length > 0 && (
                    <p className="rounded-cime border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-800">
                      {markers.length} passage{markers.length > 1 ? 's' : ''} à compléter avant l’envoi. Ils restent surlignés dans l’aperçu.
                    </p>
                  )}
                  <DocumentEditor
                    key={editorKey}
                    className="rounded-cime shadow-sm"
                    initialDoc={doc}
                    onUpdate={(next, text) => { setDoc(next); setDocText(text); }}
                  />
                </div>
              </div>
              <aside className="flex min-h-[360px] w-full min-w-0 flex-col border-t border-neutral-200 bg-neutral-50 p-3 lg:w-[46%] lg:border-l lg:border-t-0">
                {previewProps ? (
                  <LetterPreview props={previewProps} />
                ) : (
                  <div className="flex h-full min-h-[480px] items-center justify-center rounded-cime border border-dashed border-neutral-300 text-sm text-neutral-400">
                    L’aperçu s’affiche dès que le texte est prêt.
                  </div>
                )}
              </aside>
            </>
          )}
        </div>

        {doc && (
          <div className="flex items-center justify-end gap-2 border-t border-neutral-200 px-5 py-3">
            <button
              type="button"
              className="flex items-center gap-1.5 rounded border border-neutral-200 px-3 py-1.5 text-sm text-neutral-700 disabled:opacity-50"
              disabled={saveDraft.isPending || sending}
              onClick={() => persist().catch((e) => toast.error(e instanceof Error ? e.message : 'Échec de l’enregistrement.'))}
            >
              <Save className="w-4 h-4" /> Enregistrer
            </button>
            <button
              type="button"
              className="flex items-center gap-1.5 rounded bg-cime-primary px-4 py-1.5 text-sm font-medium text-white disabled:opacity-50"
              disabled={sending || markers.length > 0}
              onClick={send}
            >
              <Mail className="w-4 h-4" /> {sending ? 'Envoi…' : 'Envoyer par e-mail'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
